/**
 * `/find-a-partner` — route-specific content blocks.
 *
 * Sep 2026: the page was 223 words (Jason `thin_content`). These blocks
 * explain the partner network, the match process, local vs mail-out and
 * what partners do / do not replace, plus FAQs — all Australia-wide,
 * with "where available" hedging so no partner capability is implied
 * beyond what a region actually offers.
 */
import { Link } from "react-router-dom";
import { ArrowRight, Check, Minus } from "lucide-react";

const STEPS = [
  {
    n: "1",
    t: "Enter your suburb or postcode",
    d: "We check your area against the active AWARE partner network. Partner availability varies by region, so the result you see is specific to where you are.",
  },
  {
    n: "2",
    t: "See who covers your area",
    d: "If a partner covers your area, you'll see who they are and how to reach them. If there is no suitable local partner available, you can still book a mail-out water test.",
  },
  {
    n: "3",
    t: "Collect your sample",
    d: "Either way the sample goes to our laboratory. Mail-out water testing kits are available Australia-wide, with local drop-off available at our Noosaville lab.",
  },
  {
    n: "4",
    t: "Get your report, then act locally",
    d: "Your plain-English laboratory report shows how each result compares with relevant Australian Drinking Water Guidelines values. A local partner can help you act on it where available.",
  },
];

const CAN_HELP = [
  "Local follow-up once your results are in",
  "Tank water and bore water conversations for your property",
  "Tank cleaning services or referrals, depending on partner capability and region",
  "Filtration and UV advice where available",
  "Corrective-action pathways after a flagged result",
  "Booking and sample-kit support where available",
];

const DOES_NOT = [
  "The laboratory result itself",
  "Your Certificate of Analysis",
  "AWARE's testing and reporting process",
  "Professional advice outside their trade, licence or capability",
  "Guaranteed outcomes of any kind",
];

const FAQS = [
  {
    q: "Is partner support available everywhere in Australia?",
    a: "No — partner availability varies by region. AWARE Water Testing works with selected local partners where available, and the network is still growing. Mail-out water testing kits are available Australia-wide regardless of whether a partner covers your area.",
  },
  {
    q: "Can I still use a mail-out kit if there is no local partner?",
    a: "Yes. If there is no suitable local partner available, you can still book a mail-out water test. We post a pre-paid sample kit to you, you collect the sample and return it, and your report is emailed when the laboratory work is complete.",
  },
  {
    q: "What can a local partner help me with after my results?",
    a: "Depending on partner capability and region, that can include looking at your tank or bore on site, tank cleaning, filtration or UV options, and working through the practical next steps your report suggests. Partners work within their own trade and licence.",
  },
  {
    q: "Does the partner issue the laboratory report?",
    a: "No. Sample analysis and reporting are handled by AWARE Water Testing. The partner supports you locally before and after testing — they do not replace the laboratory result, the Certificate of Analysis or our reporting process.",
  },
  {
    q: "Will I be matched automatically?",
    a: "The search shows the partner covering your area if there is one. Nothing is booked for you automatically — you choose whether to contact the partner, book a mail-out test, or deal with AWARE directly.",
  },
  {
    q: "Can I choose to deal directly with AWARE instead?",
    a: "Yes, always. You can order a mail-out kit or book a test with us directly at any time, whether or not a partner covers your area.",
  },
];

export const FindPartnerContent = () => (
  <>
    <section className="py-14 lg:py-20 bg-white" data-testid="find-partner-about">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16">
        <div>
          <h2 className="text-2xl lg:text-3xl font-display font-bold">What the AWARE partner network is</h2>
          <p className="mt-4 text-base text-slate-600 leading-relaxed">
            AWARE Water Testing is an independent water testing laboratory
            serving households Australia-wide. Alongside our own testing, we
            work with selected local partners where available — water
            treatment and tank specialists who can help you act on your
            results in person rather than leaving you with a report and no
            next step.
          </p>
          <p className="mt-4 text-base text-slate-600 leading-relaxed">
            Partner availability varies by region, and the network does not
            cover every postcode. That is why this page never dead-ends: if
            there is no suitable local partner available, you can still book
            a mail-out water test and get the same laboratory analysis and
            plain-English report.
          </p>
        </div>
        <div>
          <h2 className="text-2xl lg:text-3xl font-display font-bold">Local partner or mail-out kit?</h2>
          <p className="mt-4 text-base text-slate-600 leading-relaxed">
            Both routes use the same laboratory and the same reporting. The
            difference is the hands-on help around the test.
          </p>
          <ul className="mt-5 space-y-3 text-sm text-slate-700">
            <li className="flex items-start gap-2.5">
              <Check className="w-4 h-4 text-cyan-600 mt-0.5 flex-shrink-0" />
              <span><strong>A local partner</strong> suits you if you want someone to look at the tank, bore or treatment gear on site, or you already know something needs fixing.</span>
            </li>
            <li className="flex items-start gap-2.5">
              <Check className="w-4 h-4 text-cyan-600 mt-0.5 flex-shrink-0" />
              <span><strong>A mail-out kit</strong> suits you if you mainly want to know what is in your water, you are outside partner coverage, or you would rather handle the sample yourself.</span>
            </li>
            <li className="flex items-start gap-2.5">
              <Check className="w-4 h-4 text-cyan-600 mt-0.5 flex-shrink-0" />
              <span>Rural and remote properties are well served by mail-out, because the kit travels to you rather than the other way around.</span>
            </li>
          </ul>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/book-water-test?kit=mail_out"
              data-testid="find-partner-content-mailout-cta"
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold px-5 py-3 text-sm transition"
            >
              Book a mail-out water test <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/bore-water-testing"
              data-testid="find-partner-content-bore-link"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 hover:border-cyan-400 text-slate-800 hover:text-cyan-700 font-semibold px-5 py-3 text-sm transition"
            >
              On bore water instead?
            </Link>
          </div>
        </div>
      </div>
    </section>

    <section className="py-14 lg:py-20 bg-slate-50" data-testid="find-partner-steps">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl lg:text-3xl font-display font-bold">How the match works</h2>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s) => (
            <div key={s.n} className="rounded-2xl bg-white border border-slate-200 p-6" data-testid={`find-partner-step-${s.n}`}>
              <div className="w-8 h-8 rounded-full bg-cyan-600 text-white text-sm font-bold grid place-items-center">{s.n}</div>
              <h3 className="mt-4 text-base font-semibold text-slate-900">{s.t}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-14 lg:py-20 bg-white" data-testid="find-partner-scope">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16">
        <div>
          <h2 className="text-2xl lg:text-3xl font-display font-bold">What AWARE partners can help with</h2>
          <ul className="mt-5 space-y-2.5">
            {CAN_HELP.map((c) => (
              <li key={c} className="flex items-start gap-2.5 text-sm text-slate-700">
                <Check className="w-4 h-4 text-cyan-600 mt-0.5 flex-shrink-0" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="text-2xl lg:text-3xl font-display font-bold">What partners do not replace</h2>
          <ul className="mt-5 space-y-2.5">
            {DOES_NOT.map((c) => (
              <li key={c} className="flex items-start gap-2.5 text-sm text-slate-700">
                <Minus className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
          <p className="mt-5 text-sm text-slate-500 leading-relaxed">
            Results are reported against relevant Australian Drinking Water
            Guidelines values. Where a result does not meet those values, the
            report explains what should be addressed — it is not a statement
            about your health, and a single test is a snapshot of the sample
            received.
          </p>
        </div>
      </div>
    </section>

    <section className="py-14 lg:py-20 bg-slate-50" data-testid="find-partner-faqs">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl lg:text-3xl font-display font-bold">Partner network FAQs</h2>
        <div className="mt-8 space-y-4">
          {FAQS.map((f, i) => (
            <div key={f.q} className="rounded-2xl bg-white border border-slate-200 p-6" data-testid={`find-partner-faq-${i}`}>
              <h3 className="text-base font-semibold text-slate-900">{f.q}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link
            to="/book-water-test"
            data-testid="find-partner-faq-book"
            className="inline-flex items-center gap-2 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 py-3 text-sm transition"
          >
            Book a water test <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/knowledge-hub"
            data-testid="find-partner-faq-learn"
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 hover:border-cyan-400 text-slate-800 hover:text-cyan-700 font-semibold px-6 py-3 text-sm transition"
          >
            Read our water guides
          </Link>
        </div>
      </div>
    </section>
  </>
);

export const FIND_PARTNER_FAQS = FAQS;
export default FindPartnerContent;
